import { useReducer, useEffect, useRef } from 'react';

const newWaypointParams = {
  id: null,
  name: '',
  description: '',
  location: {
    longitude: null,
    latitude: null
  },
  waypointDisplayProperty: {
    showTitle: true
  }
};

const organizeInitialWaypoints = waypoints => {
  return waypoints.filter(w => w.location !== null).sort((a, b) => a.order - b.order);
};

// actions
const ADD_NEW_WAYPOINT_FORM = 'ADD_NEW_WAYPOINT_FORM';
const UPDATE_EDIT_INDEX = 'UPDATE_EDIT_INDEX';
const CANCEL_WAYPOINT_FORM = 'CANCEL_WAYPOINT_FORM';
const ADD_PERSISTED_WAYPOINT = 'ADD_PERSISTED_WAYPOINT';
const REMOVE_WAYPOINT = 'REMOVE_WAYPOINT';
const TOGGLE_SORT_MODE = 'TOGGLE_SORT_MODE';
const REORDER_WAYPOINTS = 'REORDER_WAYPOINTS';
const DELETE_ALL_WAYPOINTS = 'DELETE_ALL_WAYPOINTS';
const TOGGLE_SHOW_ALL_TITLES = 'TOGGLE_SHOW_ALL_TITLES';
const TOGGLE_SHOW_TRANLSATED_LANG = 'TOGGLE_SHOW_TRANLSATED_LANG';
const UPDATE_FOR_INCOMING_WAYPOINTS = 'UPDATE_FOR_INCOMING_WAYPOINTS';
const UPDATE_WAYPOINT_EDIT_PARAMS = 'UPDATE_WAYPOINT_EDIT_PARAMS';

// reducer
const waypointsReducer = (state, action) => {
  switch (action.type) {
    case ADD_NEW_WAYPOINT_FORM:
      return {
        ...state,
        waypoints: [...state.waypoints, newWaypointParams],
        editIndex: state.waypoints.length
      };
    case UPDATE_EDIT_INDEX:
      return {
        ...state,
        editIndex: action.payload
      };
    case CANCEL_WAYPOINT_FORM:
      return {
        ...state,
        editIndex: null,
        editParams: null,
        waypoints: state.waypoints.filter(w => w.id !== null)
      };
    case REMOVE_WAYPOINT:
      return {
        ...state,
        waypoints: state.waypoints.filter(wp => wp.id !== action.payload)
      };
    case TOGGLE_SORT_MODE:
      return {
        ...state,
        sortMode: action.payload,
        editIndex: null
      };
    case ADD_PERSISTED_WAYPOINT:
      return {
        ...state,
        editIndex: null,
        editParams: null,
        waypoints: state.waypoints.map((wp, idx) => {
          if (idx === state.editIndex) {
            return { ...action.payload };
          }

          return wp;
        })
      };
    case REORDER_WAYPOINTS:
      return { ...state, waypoints: action.payload };
    case DELETE_ALL_WAYPOINTS:
      return { ...state, waypoints: [] };
    case TOGGLE_SHOW_ALL_TITLES:
      return {
        ...state,
        waypoints: state.waypoints.map(wp => {
          return { ...wp, waypointDisplayProperty: { showTitle: action.payload } };
        })
      };
    case TOGGLE_SHOW_TRANLSATED_LANG:
      return {
        ...state,
        waypoints: state.waypoints.map((wp, idx) => {
          if (idx === action.payload.idx) {
            return { ...wp, showInTranslatedLang: action.payload.showInTranslatedLang };
          }

          return wp;
        })
      };
    case UPDATE_FOR_INCOMING_WAYPOINTS:
      return {
        ...state,
        editIndex: null,
        waypoints: [...organizeInitialWaypoints(action.payload)]
      };
    case UPDATE_WAYPOINT_EDIT_PARAMS: {
      return {
        ...state,
        editParams: action.payload
      };
    }

    default:
      return state;
  }
};

const useWaypoints = ({ initialWaypoints = [], messagingChannel = null, canEdit = false }) => {
  const initialState = {
    waypoints: organizeInitialWaypoints(initialWaypoints),
    newWaypointParams,
    editIndex: null,
    sortMode: false,
    editParams: null
  };

  const [state, dispatch] = useReducer(waypointsReducer, { ...initialState });
  const prevStateRef = useRef(state);

  if (!canEdit) {
    return [initialState, null];
  }

  useEffect(() => {
    if (!messagingChannel || !canEdit) {
      return;
    }

    const { editIndex, waypoints } = state;
    const activeWaypoint = waypoints[editIndex];
    const editWaypointId = typeof activeWaypoint === 'undefined' ? 0 : activeWaypoint.id;

    // calls waypointsChanged in search_app to keep waypoints consistent everywhere
    messagingChannel.publish({
      topic: 'waypointsChanged',
      data: { waypoints, editWaypointId, activeWaypoint }
    });
  }, [state.editIndex, state.waypoints]);

  const dispatchWaypoints = ({ type, payload = {} }) => {
    dispatch({ type, payload });
  };

  useEffect(() => {
    prevStateRef.current = state;
  }, [state]);

  return [state, dispatchWaypoints];
};

export {
  useWaypoints,
  ADD_NEW_WAYPOINT_FORM,
  ADD_PERSISTED_WAYPOINT,
  CANCEL_WAYPOINT_FORM,
  DELETE_ALL_WAYPOINTS,
  REMOVE_WAYPOINT,
  REORDER_WAYPOINTS,
  TOGGLE_SHOW_ALL_TITLES,
  TOGGLE_SHOW_TRANLSATED_LANG,
  TOGGLE_SORT_MODE,
  UPDATE_EDIT_INDEX,
  UPDATE_FOR_INCOMING_WAYPOINTS,
  UPDATE_WAYPOINT_EDIT_PARAMS
};
